import React, { useState, useEffect } from 'react';
import axios from 'axios';
import useLogger from 'hooks/useLogger';
import { Button, Modal } from '@dsg-tech/homefield-react';
import { STORE_INFO_URL, setAOSStoreAnalytics } from 'utility/utility';
import { setCookie, getCookie } from 'utility/cookie-utils';
import { useAppDispatch, useAppSelector } from 'store/store';
import { headerTaxoSelector } from 'store/selectors/taxonomySelector';
import ErrorBoundaryWithLogger from 'components/common/ErrorBoundary/ErrorBoundary';
import { setAthleteZip, setDeliveryZip } from 'store/slices/locationServices';
import AOSOffers from './AOSOffers/AOSOffers';
import './AOS.scss';
import StoreSelectionForm from './StoreSelectionForm';

const AssociateOrderingSystem = () => {
  const dispatch = useAppDispatch();
  const taxonomy = useAppSelector(headerTaxoSelector);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [storeNumber, setStoreNumber] = useState<number | null>(null);
  const [storeData, setStoreData] = useState<{ store: number; name: string } | null>(null);
  const [storeError, setStoreError] = useState<string | null>(null);
  const [associateId, setAssociateId] = useState<string | null>(null);
  const [validateFormOnClose, setValidateFormOnClose] = useState<boolean>(false);
  const currentOffers = taxonomy['current offers']?.[0];
  const { warn } = useLogger();

  const storeAnalytics = () => {
    const analyticsStoreData = {
      deviceType: 'AOS',
      storeNumber,
    };
    setAOSStoreAnalytics(analyticsStoreData);
  };

  useEffect(() => {
    if (storeNumber && storeData) {
      if (window.location.pathname === '/') {
        storeAnalytics();
      }
    } else {
      const numberCookie = getCookie('AOS_STORENBR');
      const nameCookie = getCookie('AOS_STORENAME');
      const associateIdCookie = getCookie('AOS_ASSOCIATENBR');

      if (numberCookie && nameCookie) {
        const num = parseInt(numberCookie, 10);
        setStoreNumber(num);
        setStoreData({ store: num, name: nameCookie });

        if (associateIdCookie) {
          setAssociateId(associateIdCookie);
        }
      } else {
        setModalOpen(true);
      }
    }
  }, [storeNumber, storeData]);

  const handleStoreChange = (newStore: number, newAssociateId: string) => {
    setStoreError(null);
    axios(STORE_INFO_URL + newStore, {
      method: 'GET',
      timeout: 5000,
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((response) => {
        if (response.status === 200 && response.data) {
          setStoreData(response.data);
          setStoreNumber(newStore);
          setAssociateId(newAssociateId);
          setModalOpen(false);
          setValidateFormOnClose(false);

          setCookie('AOS_ASSOCIATENBR', newAssociateId);
          setCookie('deliveryZip', response.data.zipcode);
          setCookie('AOS_STORENBR', `${response.data.store}`);
          setCookie('AOS_STORENAME', response.data.name);
          dispatch(setDeliveryZip(response.data.zipcode));
          dispatch(setAthleteZip(response.data.zipcode));
          setCookie(
            'setStoreCookie',
            `${response.data.zipcode}_${response.data.name}_${response.data.store}_${response.data.status}`,
          );
          setCookie(
            'DCSG_NGX_CUST_STORE',
            `${response.data.zipcode}_${response.data.name}_${response.data.store}_${response.data.status}`,
          );
          storeAnalytics();
          const event = new CustomEvent('aosStoreInformationPopupCompleted', {
            detail: { StoreNumber: newStore.toString() },
          });
          document.dispatchEvent(event);
        } else {
          setStoreError(`Store number ${newStore} is invalid. Please enter correct store number.`);
        }
      })
      .catch((e) => {
        warn('AOS', { ErrorMessage: 'Error fetching store data', Error: e });
        setStoreError(`Store number ${newStore} is invalid. Please enter correct store number.`);
      });
  };

  const removeCookies = () => {
    document.cookie.split(';').forEach((cookie) => {
      document.cookie = cookie
        .replace(/^ +/, '')
        .replace(/=.*/, `=;expires=${new Date().toUTCString()};path=/`);
    });
  };

  const endSession = () => {
    removeCookies();
    localStorage.clear();
    sessionStorage.clear();
    window.location.href = '/';
  };

  // Prevent closing unless data is valid
  const handleCloseModal = () => {
    if (!storeData) {
      // Show errors for both fields if they are empty
      // Setting validateFormOnClose to true to trigger validation in the form component
      setValidateFormOnClose((prev) => !prev);
      return;
    }
    setModalOpen(false);
    setValidateFormOnClose(false);
  };

  const handleStoreInputChange = () => {
    if (storeError) {
      setStoreError(null);
    }
  };

  return (
    <div className="aos-header-container">
      <ErrorBoundaryWithLogger componentId="AOS-Modal">
        <Modal
          open={modalOpen}
          closable
          onCloseModal={handleCloseModal}
          title="Store Information"
          stickyCloseButton
          className="aos-modal">
          <div className="store-modal-content hmf-p-m">
            <StoreSelectionForm
              onSubmit={handleStoreChange}
              error={storeError}
              validateOnParentRequest={validateFormOnClose}
              onStoreInputChange={handleStoreInputChange}
            />
          </div>
        </Modal>
      </ErrorBoundaryWithLogger>
      <div className="aos-content hmf-display-flex hmf-flex-row hmf-justify-content-between hmf-py-xs hmf-px-m">
        <div className="aos-actions hmf-display-flex hmf-align-items-flex-start hmf-flex-column">
          <p className="aos-title hmf-header-s hmf-mb-xs hmf-mt-0">Associate Ordering System</p>
          <Button
            variant="secondary"
            text="END SESSION & CLEAR INFO"
            onClick={endSession}
            className="aos-button hmf-body-bold-l"
            size="large"
          />
        </div>
        <div className="aos-store-info hmf-display-flex hmf-flex-column hmf-align-items-flex-end">
          <p className="aos-store-details hmf-header-s hmf-mb-xs hmf-mt-0">{`${
            storeData ? `${storeData.name}` : 'No Store Selected'
          }`}</p>
          <p className="aos-store-number hmf-body-l hmf-m-0">{`Store Number: ${storeNumber}`}</p>
          <p className="aos-associate-id hmf-body-l hmf-m-0">{`Employee ID: ${associateId}`}</p>
        </div>
      </div>
      <ErrorBoundaryWithLogger componentId="AOS-AOSOffers">
        {currentOffers && <AOSOffers currentOffers={currentOffers} />}
      </ErrorBoundaryWithLogger>
    </div>
  );
};

export default AssociateOrderingSystem;
